import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import { useSelector } from "react-redux";
import loadable from "@loadable/component";
import ClientOnly from "../ClientOnly";
import Results from "../Results";
import LayoutManager from "../LayoutManager";
import SearchTerm from "../SearchTerm";
import styles from "./ProductListingPage.css";
import Facets from "../Facets";
import { DELIMITER } from "../ProductTile/ariaConstants";
import {
  getItemCount,
  hasSearchFacets,
  selectSearchIsNewSearch,
  selectSearchIsInitialLoading
} from "../../state/modules/search";
import { getError } from "../../state/modules/error";
import { getViewportCategory } from "../../state/modules/layout/selectors";
import { useConfigByStore, useConfigByCountry } from "../hooks";
import {
  EU_OMNIBUS_COUNTRIES_KEY,
  DEAL_BANNER_STORES_KEY
} from "../../config/constants";

const Faqs = loadable(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CATEGORY_FOOTER" */ "../Faqs"
    ),
  { fallback: null }
);

const SecondaryCategorySeoLinks = loadable(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CATEGORY_FOOTER" */ "../SecondaryCategorySeoLinks"
    ),
  { fallback: null }
);

const CategoryFooterDescription = loadable(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CATEGORY_FOOTER" */ "../CategoryFooterDescription"
    ),
  { fallback: null }
);

const ErrorAlert = loadable(
  () => import(/* webpackChunkName: "ERROR_ALERT" */ "../ErrorAlert"),
  { fallback: null }
);

const styleContext = classNames.bind(styles);

export const ProductListingPageContent = props => {
  const { hasFacets, isNewSearch, location, viewportCategory } = props;
  const search = location?.search ?? "";
  const queryStringRef = useRef(search);
  const { current: queryString } = queryStringRef;

  useEffect(() => {
    if (queryString !== search) {
      queryStringRef.current = search;

      if (isNewSearch) {
        window.scrollTo(0, 0);
      }
    }
  }, [search]);

  return (
    <div
      className={styleContext({
        container: true,
        wrapper: hasFacets
      })}
    >
      <LayoutManager />
      <SearchTerm />
      {hasFacets && <Facets viewportCategory={viewportCategory} />}
      <Results {...props} />
      <Faqs />
      <SecondaryCategorySeoLinks />
      <CategoryFooterDescription />
      <ClientOnly>
        <ErrorAlert />
      </ClientOnly>
      <span className={styles.ariaDelimiter} id={DELIMITER}>
        ;
      </span>
    </div>
  );
};

ProductListingPageContent.propTypes = {
  isNewSearch: PropTypes.bool,
  location: PropTypes.object,
  hasFacets: PropTypes.bool.isRequired,
  viewportCategory: PropTypes.string.isRequired,
  upSellCarousel: PropTypes.node,
  itemCount: PropTypes.number,
  error: PropTypes.any,
  isLoading: PropTypes.bool,
  dealBannerEnabled: PropTypes.bool,
  euOmnibusEnabled: PropTypes.bool,
  pages: PropTypes.arrayOf(PropTypes.object),
  isErrorOfTypeServer: PropTypes.bool,
  isLoadingPrevious: PropTypes.bool,
  offset: PropTypes.object,
  tileToFocus: PropTypes.object,
  advert: PropTypes.func
};

const ProductListingPage = ownProps => {
  const itemCount = useSelector(getItemCount);
  const error = useSelector(getError);
  const isNewSearch = useSelector(selectSearchIsNewSearch);
  const hasFacets = useSelector(hasSearchFacets);
  const isLoading = useSelector(selectSearchIsInitialLoading);
  const viewportCategory = useSelector(getViewportCategory);

  const euOmnibusEnabled = useConfigByCountry({
    configKey: EU_OMNIBUS_COUNTRIES_KEY
  });
  const dealBannerEnabled = useConfigByStore({
    configKey: DEAL_BANNER_STORES_KEY
  });

  const props = {
    itemCount,
    error,
    isNewSearch,
    hasFacets,
    isLoading,
    dealBannerEnabled,
    euOmnibusEnabled,
    viewportCategory
  };

  return <ProductListingPageContent {...{ ...ownProps, ...props }} />;
};

export default ProductListingPage;
